import { Link, usePage } from "@inertiajs/react"
import { useState } from "react"

import FoldRow from "../../../shared/FoldRow"
import Layout from "../../../shared/Layout"
import type { SharedData } from "../../../shared/shared"
import { useTripClock } from "../../../shared/tripClock"

interface DayCard {
  id: number
  dayOfMonth: string
  monthAbbr: string
  title: string
  subtitle: string
  // t015 `impl-clock` (FR-2.2, FR-2.5): replaces the withdrawn `isToday` —
  // the card's own ISO date, compared against the trip clock's own `today`
  // (FR-1.5) to decide the accent/pill treatment on the device, not here.
  date: string
  href: string
}

interface DaysProps {
  dateLine: string
  days: DayCard[]
  // t015 `impl-main` (FR-3.1, FR-3.5): pastDays/hidePast are the fold
  // row's own two labels, alongside the pre-existing today pill label.
  labels: { today: string; pastDays: string; hidePast: string }
}

type CardVariant = "default" | "today" | "past"

const LINK_CLASSES: Record<CardVariant, string> = {
  default: "flex min-h-16 items-center gap-4 rounded-xl border border-gray-200 px-4 py-3",
  today:
    "flex min-h-16 items-center gap-4 rounded-xl border-[1.5px] border-accent bg-accent/5 px-4 py-3",
  // t015 `impl-main` (FR-3.8): the contract's stepped-down ink
  // (docs/design/main-page.html:262-274) — same card anatomy, still the
  // same whole-card Link to its day page.
  past: "flex min-h-16 items-center gap-4 rounded-xl border border-gray-100 px-4 py-3",
}

interface DayCardRowProps {
  day: DayCard
  today: string
  todayLabel: string
}

// One row of the day list: a normal card, today's accent card, or a past
// card's stepped-down ink (FR-3.8) — the same anatomy throughout, still the
// same whole-card Link to its day page (FR-23, t008). Extracted once the
// per-variant class picking made the parent's JSX too deep to read inline
// (inertia-react-standards: extract only when it earns its keep).
function DayCardRow({ day, today, todayLabel }: DayCardRowProps) {
  const isToday = day.date === today
  // t015 `impl-main` (FR-1.5): a plain string comparison on the ISO `date`
  // key, exactly like the `today` comparison above — no parsing, no date
  // library.
  const isPast = day.date < today
  const variant: CardVariant = isToday ? "today" : isPast ? "past" : "default"

  return (
    <li>
      <Link href={day.href} className={LINK_CLASSES[variant]}>
        <div className="flex w-11 flex-col items-center">
          <div
            className={`text-[26px] font-bold leading-[30px] tabular-nums ${
              isPast ? "text-gray-400" : "text-accent"
            }`}
          >
            {day.dayOfMonth}
          </div>
          <div className={`text-xs ${isPast ? "text-gray-300" : "text-gray-500"}`}>
            {day.monthAbbr}
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-0.5">
          <div
            className={`text-base font-semibold ${isPast ? "text-gray-500" : "text-gray-900"}`}
          >
            {day.title}
          </div>
          <div className={`text-[13px] leading-[18px] ${isPast ? "text-gray-300" : "text-gray-500"}`}>
            {day.subtitle}
          </div>
        </div>
        {isToday && (
          <div className="rounded-full bg-accent px-2.5 py-[3px] text-xs font-medium text-white">
            {todayLabel}
          </div>
        )}
        {/* FR-5.6, AC-16: decorative only — hidden from assistive
            technology, and not a separately focusable control; the card
            <Link> itself is the one focusable, keyboard-reachable tap
            target. */}
        <svg
          aria-hidden="true"
          className={isPast ? "text-gray-200" : "text-gray-300"}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M7.5 5l5 5-5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Link>
    </li>
  )
}

// The application's main page — the «Расписание» tab (Schedule::DaysController
// renders it at the root route). Props mirror exactly what the controller
// sends (no `any`, no untyped spread); trip title and the shell strings
// arrive as shared data through Layout, not here. Implements
// docs/design/main-page.html verbatim (FR-10): same Tailwind classes, same
// element structure, minus the burger menu and photo strip (out of scope).
//
// FR-23, t008: the whole card is the tap target, not just the chevron — the
// card's classes moved from the <li> onto an Inertia <Link> wrapping its
// content, and the bare <li> contributes nothing to the contract's markup.
// The chevron stays decorative (aria-hidden) and gains nothing else: it was
// never a separately focusable control and still isn't (AC-16).
//
// t015 `impl-clock` (FR-3.10): today's accent border/tint and pill are no
// longer a server-sent `isToday` flag — they are derived here, on the
// device, from the trip clock's own `today` (`useTripClock`, read through
// the shared `tripTimezone` prop) compared against each card's own `date`
// (FR-1.5). Exactly one card carries the treatment when the trip-local
// today falls inside the trip; none does when it falls outside (AC-6).
export default function Days({ dateLine, days, labels }: DaysProps) {
  const {
    props: { tripTimezone },
  } = usePage<SharedData>()
  const { today } = useTripClock(tripTimezone)
  // t015 `impl-main` (FR-3.6): the fold state lives in this component only
  // — no URL, no storage — so navigating away and back always remounts
  // folded.
  const [open, setOpen] = useState(false)

  // t015 `impl-main` (FR-1.5, FR-3.1-FR-3.3): the already date-ordered
  // list, partitioned once by a plain string comparison. The row renders
  // only when both halves are non-empty — mid-trip — which yields "no row
  // before the trip" and "no row after it" with no special-casing either
  // edge (FR-3.2, FR-3.3): before the trip `past` is empty, after it
  // `rest` is.
  const past = days.filter((day) => day.date < today)
  const rest = days.filter((day) => day.date >= today)
  const showFold = past.length > 0 && rest.length > 0
  // Folded renders `rest` only; unfolded (or no fold at all) renders
  // `days` whole, so chronological order is always inherited, never
  // re-sorted (FR-3.5).
  const visibleDays = showFold && !open ? rest : days

  return (
    <Layout>
      <p className="text-sm text-gray-500">{dateLine}</p>
      {/* FR-5.1: the list comes from Day records only — no card list at all
          when there are none, rather than a synthesized placeholder per
          calendar date (AC-7). */}
      {days.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {showFold && (
            // t021 `impl-fold-row`: the row itself moved to the shared
            // module (FR-14) — this page keeps the <li> wrapper the row no
            // longer renders, so the list's own markup is unchanged.
            <li>
              <FoldRow
                open={open}
                onToggle={() => setOpen((value) => !value)}
                dates={past}
                labels={{ collapsed: labels.pastDays, expanded: labels.hidePast }}
              />
            </li>
          )}
          {visibleDays.map((day) => (
            <DayCardRow key={day.id} day={day} today={today} todayLabel={labels.today} />
          ))}
        </ul>
      )}
    </Layout>
  )
}
